import type { ProvaCompetenzaDto, ProvaRuoliDto, ProvaRuoloDto, TimoneLevel } from '@bk/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ApiRequestError, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useSinapsiFonts } from '../tenant/wizard/sinapsi-fonts';
import '../tenant/wizard/timone.css';
import './prova.css';

/**
 * La prova dei ruoli (contratto n. 2, art. 6.8; ADR 0173): pagina pubblica, basta il link di prova del titolare. Chi la
 * apre non si identifica: sceglie il suo ruolo e vede la dashboard d'esempio del ruolo (senza euro), le competenze della
 * scheda della professione con cosa cambia con Timonio, la crescita; può segnare le competenze (la spunta resta in questo
 * browser) e proporre al titolare di acquistare, con il ruolo e una riga, mai il nome.
 */

const LIVELLO: Record<TimoneLevel, string> = { direzione: 'direzione', responsabile: 'responsabile', operativo: 'operativo' };
const GRUPPO: Record<ProvaCompetenzaDto['gruppo'], string> = { conoscenza: 'conoscenza', abilita: 'abilità', mestiere: 'competenza del mestiere' };
const h = (n: number) => `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ore`;

type Segno = 'ho' | 'crescere';
type Spunte = Record<string, Segno>;

async function provaApi<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1/prova/${path}`, body === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({ code: 'invalid_response', message: 'Risposta non valida' }));
  if (!res.ok) throw new ApiRequestError(res.status, data);
  return data as T;
}

/** La spunta resta nel browser della persona (art. 6.8.4): mai sul server. */
function useSpunte(tenantId: string, ruolo: string): [Spunte, (testo: string, segno: Segno) => void] {
  const key = `prova-ruoli:${tenantId}:${ruolo}`;
  const [spunte, setSpunte] = useState<Spunte>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? '{}') as Spunte;
    } catch {
      return {};
    }
  });
  const segna = (testo: string, segno: Segno) => {
    const next = { ...spunte };
    if (next[testo] === segno) delete next[testo];
    else next[testo] = segno;
    setSpunte(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* senza memoria del browser la spunta vale solo finché la pagina è aperta */
    }
  };
  return [spunte, segna];
}

export function ProvaRuoliPage({ tenantId, token, ruolo }: { tenantId: string; token: string; ruolo: string | null }) {
  useSinapsiFonts();
  const q = useQuery({
    queryKey: ['prova', tenantId, token, ruolo],
    queryFn: () => provaApi<ProvaRuoliDto>(`${tenantId}/${token}${ruolo ? `?ruolo=${encodeURIComponent(ruolo)}` : ''}`),
    retry: (n, e) => !(e instanceof ApiRequestError && e.status === 404) && n < 3,
    refetchInterval: (x) => (x.state.data?.scelto?.in_preparazione ? 5000 : false),
  });
  useEffect(() => {
    document.title = q.data ? `Prova Timonio · ${q.data.azienda.nome}` : 'Prova Timonio';
  }, [q.data]);
  const base = `/prova/${tenantId}/${token}`;
  if (q.error)
    return (
      <div className="tm pv">
        <div className="pv-wrap">
          <span className="tm-ey">Prova dei ruoli</span>
          <h2>Questo link non apre più nulla</h2>
          <p className="tm-sub">{errorMessage(q.error)}. Chiedi al titolare un link nuovo.</p>
        </div>
      </div>
    );
  const d = q.data;
  if (!d) return <div className="tm pv" aria-busy />;
  return (
    <div className="tm pv" aria-label="Prova dei ruoli">
      <header className="pv-top">
        <div>
          <span className="tm-ey">Prova con dati d’esempio · {d.azienda.nome}{d.azienda.luogo ? ` · ${d.azienda.luogo}` : ''}</span>
        </div>
        <span className="tm-tag">nessun dato tuo viene conservato</span>
      </header>
      {d.scelto ? <Ruolo d={d.scelto} tenantId={tenantId} token={token} base={base} /> : <Scelta d={d} base={base} />}
      <p className="tm-foot">
        Questa è una prova: le persone, le date e i volumi sono esempi costruiti sul progetto della tua azienda, mai dati veri. Timonio non sa chi sei e non conserva nulla di te: conta solo che qualcuno ha provato questo ruolo. Timonio propone un assetto ideale con le fonti; non sostituisce il consulente del lavoro, l’RSPP, il commercialista o l’avvocato.
      </p>
    </div>
  );
}

function Scelta({ d, base }: { d: ProvaRuoliDto; base: string }) {
  const gruppi = useMemo(() => {
    const m = new Map<string, ProvaRuoliDto['ruoli']>();
    for (const r of d.ruoli) {
      const k = [r.sede, r.reparto].filter(Boolean).join(' · ') || 'Impresa';
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()];
  }, [d.ruoli]);
  return (
    <section className="pv-wrap">
      <h2>Qual è il tuo ruolo in {d.azienda.nome}?</h2>
      <p className="tm-sub">Scegli quello che fai, o il più vicino. Vedrai la tua giornata con Timonio e cosa cambia per ciò che sai fare. Niente nome, niente email: puoi anche cambiare ruolo dopo.</p>
      {gruppi.length === 0 && <p className="tm-grey">I ruoli compaiono appena l’organigramma della fase 8 è pronto.</p>}
      {gruppi.map(([nome, ruoli]) => (
        <div key={nome} className="pv-gruppo">
          <span className="tm-ey">{nome}</span>
          <div className="pv-ruoli">
            {ruoli.map((r) => (
              <button key={r.ruolo} type="button" className="pv-ruolo" onClick={() => navigate(`${base}/${encodeURIComponent(r.ruolo)}`)}>
                <b>{r.ruolo}</b>
                <small>{LIVELLO[r.livello]}</small>
              </button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function Ruolo({ d, tenantId, token, base }: { d: ProvaRuoloDto; tenantId: string; token: string; base: string }) {
  const [spunte, segna] = useSpunte(tenantId, d.ruolo);
  const [avvisata, setAvvisata] = useState(false);
  const x = d.dashboard;
  const peso = (c: ProvaCompetenzaDto) => (spunte[c.testo] === 'crescere' ? 0 : spunte[c.testo] === 'ho' ? 2 : 1);
  const competenze = [...d.competenze].sort((a, b) => peso(a) - peso(b));
  const toccate = Object.keys(spunte).length;
  return (
    <>
      <section className="pv-wrap pv-hero">
        <button type="button" className="pv-back" onClick={() => navigate(base)}>
          ← Cambia ruolo
        </button>
        <span className="tm-ey">
          {d.ruolo} · {LIVELLO[d.livello]}
          {d.reparto ? ` · ${d.reparto}` : ''}
          {d.sede ? `, ${d.sede}` : ''}
        </span>
        <h2>{d.testi.titolo}</h2>
        <p className="tm-sub">{d.testi.sottotitolo}</p>
        {d.professione && <p className="tm-note">Professione di riferimento: {d.professione} (scheda della professione, fase 8).</p>}
      </section>

      <section className="tm-sec">
        <div className="tm-head">
          <h3>La tua giornata con Timonio</h3>
          <p>Un esempio costruito sulle procedure, le scadenze e i programmi del tuo ruolo in questa azienda.</p>
        </div>
        <div className="tm-app" role="region" aria-label="La dashboard d’esempio del ruolo">
          <div className="tm-app-bar">
            <span>
              <b>Buongiorno</b> · {x.ruolo}
              {x.reparto ? ` · ${x.reparto}` : ''}
              {x.sede ? `, ${x.sede}` : ''}
            </span>
            <span className="tm-tag">esempio</span>
          </div>
          <div className="tm-app-body">
            <div className="tm-col">
              {x.oggi[0] && (
                <div className="tm-first">
                  <small>La prima cosa da fare</small>
                  <p>{x.oggi[0].testo}</p>
                </div>
              )}
              <h5>Cosa vedi ogni mattina</h5>
              <ul className="tm-list">
                {d.vede.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
              {x.scadenze.length > 0 && (
                <>
                  <h5>Scadenze di cui rispondi</h5>
                  <ul className="tm-list">
                    {x.scadenze.slice(0, 3).map((s) => (
                      <li key={s.testo}>
                        {s.testo}
                        {s.quando ? ` · ${s.quando}` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="tm-col">
              {x.benefici?.ore_mese ? (
                <div className="tm-kpi">
                  <b>{h(x.benefici.ore_mese)}</b>
                  <span>al mese che tornano al tuo ruolo</span>
                </div>
              ) : null}
              <h5>Cosa fai grazie a Timonio</h5>
              <ul className="tm-list tm-do">
                {d.fa.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
              {x.in_arrivo.length > 0 && <p className="tm-note">In arrivo: {x.in_arrivo.join('; ')}.</p>}
            </div>
          </div>
        </div>
      </section>

      <section className="tm-sec">
        <div className="tm-head">
          <h3>Le tue competenze e cosa cambia</h3>
          <p>
            Dalla scheda della professione del tuo ruolo, con la fonte. Per ognuna, cosa cambia con Timonio; dove non c’entra, lo diciamo.
            {toccate === 0 && ' Puoi segnare quelle che hai già e quelle che vuoi far crescere: la spunta resta in questo browser, non arriva a Timonio né al titolare.'}
          </p>
        </div>
        {competenze.length === 0 ? (
          <p className="tm-grey">Per questo ruolo la libreria non ha ancora una scheda della professione: le competenze compaiono appena c’è.</p>
        ) : (
          <ul className="pv-comp">
            {competenze.map((c) => (
              <li key={c.testo} className={`pv-c${spunte[c.testo] ? ` ${spunte[c.testo]}` : ''}`}>
                <div className="pv-c-head">
                  <span className="tm-ey">{GRUPPO[c.gruppo]}</span>
                  <span className="pv-fonte">{c.fonte}</span>
                </div>
                <b>{c.testo}</b>
                {c.cambia ? (
                  <p className="pv-cambia">
                    {c.cambia}
                    {c.voce && <small>dalla voce «{c.voce}»</small>}
                  </p>
                ) : (
                  <p className="pv-resta">Resta tua: qui Timonio non c’entra.</p>
                )}
                <div className="pv-segni" role="group" aria-label={`Segna «${c.testo}»`}>
                  <button type="button" aria-pressed={spunte[c.testo] === 'ho'} onClick={() => { setAvvisata(true); segna(c.testo, 'ho'); }}>
                    La ho già
                  </button>
                  <button type="button" aria-pressed={spunte[c.testo] === 'crescere'} onClick={() => { setAvvisata(true); segna(c.testo, 'crescere'); }}>
                    Voglio farla crescere
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {avvisata && toccate > 0 && <p className="tm-note">Le tue spunte sono solo in questo browser: Timonio e il titolare non le vedono. Prima vengono quelle che vuoi far crescere.</p>}
        {(d.crescita.da_aggiornare.length > 0 || d.crescita.ruoli_vicini.length > 0) && (
          <div className="pv-crescita">
            <span className="tm-ey">Per crescere</span>
            {d.crescita.da_aggiornare.length > 0 && (
              <p>
                Da aggiornare, secondo chi fa questo mestiere: {d.crescita.da_aggiornare.map((c) => c.testo).join('; ')}.
                <small> {d.crescita.da_aggiornare[0]?.fonte}</small>
              </p>
            )}
            {d.crescita.ruoli_vicini.length > 0 && <p>Ruoli vicini al tuo: {d.crescita.ruoli_vicini.join(', ')}.</p>}
          </div>
        )}
        {d.in_preparazione && <p className="tm-grey">Il consulente di Timonio sta scrivendo i testi per il tuo ruolo: la pagina si aggiorna da sola.</p>}
      </section>

      <Proposta tenantId={tenantId} token={token} ruolo={d.ruolo} />
    </>
  );
}

function Proposta({ tenantId, token, ruolo }: { tenantId: string; token: string; ruolo: string }) {
  const [riga, setRiga] = useState('');
  const m = useMutation({ mutationFn: () => provaApi<void>(`${tenantId}/${token}/proponi`, { ruolo, ...(riga.trim() ? { riga: riga.trim() } : {}) }) });
  return (
    <section className="tm-close pv-prop">
      <div>
        <span className="tm-ey">Proponi al titolare</span>
        <h3 style={{ marginTop: 10 }}>Ti servirebbe?</h3>
        {m.isSuccess ? (
          <div className="tm-done" role="status">
            <b>Proposta inviata al titolare.</b>
            <p>Gli arriva con il tuo ruolo e la tua riga, senza il tuo nome. Deciderà lui se e quando prendere Timonio.</p>
          </div>
        ) : (
          <>
            <p>Puoi dire al titolare che la vuoi: gli arriva il ruolo «{ruolo}», la data e, se vuoi, una riga. Non scrivere il tuo nome: se questo ruolo lo fa una persona sola, il titolare capirà comunque chi sei.</p>
            <textarea className="pv-riga" maxLength={300} rows={2} placeholder="Una riga, se vuoi (facoltativa)" value={riga} onChange={(e) => setRiga(e.target.value)} />
            <div style={{ marginTop: 14 }}>
              <button type="button" className="tm-buy" disabled={m.isPending} onClick={() => m.mutate()}>
                ⎈ {m.isPending ? 'Invio…' : 'Proponi al titolare'}
              </button>
            </div>
            {m.error && <p className="tm-note" style={{ color: '#ff9b9b', marginTop: 10 }}>{errorMessage(m.error)}</p>}
          </>
        )}
      </div>
    </section>
  );
}
